import React, { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { Phone, Mail, MapPin, Calendar, CheckCircle } from "lucide-react";
import { toast } from "sonner";
import { Header, Footer } from "../components/common";
import { BookingModal } from "../components/booking";
import { useBooking } from "../context";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { API } from "../lib/constants";

const ContactPage = () => {
  const { setShowBookingModal } = useBooking();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: ""
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/contact`, formData);
      if (response.data.success) {
        setSubmitted(true);
        toast.success("Message sent! We'll get back to you soon.");
      }
    } catch (error) {
      toast.error("Failed to send message. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950" data-testid="contact-page">
      <Header />
      
      <section className="pt-32 pb-20 px-4 md:px-8">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-12"
          >
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              Contact <span className="gradient-text">Us</span>
            </h1>
            <p className="text-slate-400 text-lg">
              Get in touch with us for any questions or to book your detail
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 }}
              className="glass rounded-2xl p-8"
            >
              <h2 className="text-2xl font-bold mb-6">Get In Touch</h2>
              
              <div className="space-y-6">
                <a href="tel:0478054648" className="flex items-center gap-4 text-slate-300 hover:text-cyan-400 transition">
                  <div className="w-12 h-12 rounded-full bg-cyan-400/10 flex items-center justify-center">
                    <Phone className="w-5 h-5 text-cyan-400" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Call or Text</p>
                    <p className="font-medium">0478 054 648</p>
                  </div>
                </a>
                
                <a href="mailto:hello.rainxpress@gmail.com" className="flex items-center gap-4 text-slate-300 hover:text-cyan-400 transition">
                  <div className="w-12 h-12 rounded-full bg-cyan-400/10 flex items-center justify-center">
                    <Mail className="w-5 h-5 text-cyan-400" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Email</p>
                    <p className="font-medium">hello.rainxpress@gmail.com</p>
                  </div>
                </a>
                
                <div className="flex items-center gap-4 text-slate-300">
                  <div className="w-12 h-12 rounded-full bg-cyan-400/10 flex items-center justify-center">
                    <MapPin className="w-5 h-5 text-cyan-400" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Location</p>
                    <p className="font-medium">Thomastown, VIC</p>
                  </div>
                </div>
              </div>

              <div className="mt-8">
                <Button 
                  onClick={() => setShowBookingModal(true)}
                  className="w-full btn-primary-glow text-slate-900 font-semibold py-3 rounded-full"
                >
                  <Calendar className="w-4 h-4 mr-2" />
                  Book Now
                </Button>
              </div>
            </motion.div>

            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
              className="glass rounded-2xl p-8"
            >
              <h2 className="text-2xl font-bold mb-6">Send a Message</h2>
              
              {!submitted ? (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label className="text-sm mb-2 block">Name</Label>
                    <Input
                      placeholder="Your name"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      className="bg-slate-800 border-slate-700"
                      required
                    />
                  </div>
                  <div>
                    <Label className="text-sm mb-2 block">Email</Label>
                    <Input
                      type="email"
                      placeholder="your@email.com"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      className="bg-slate-800 border-slate-700"
                      required
                    />
                  </div>
                  <div>
                    <Label className="text-sm mb-2 block">Phone</Label>
                    <Input
                      placeholder="0400 000 000"
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      className="bg-slate-800 border-slate-700"
                    />
                  </div>
                  <div>
                    <Label className="text-sm mb-2 block">Message</Label>
                    <Textarea
                      placeholder="How can we help?"
                      value={formData.message}
                      onChange={(e) => setFormData({...formData, message: e.target.value})}
                      className="bg-slate-800 border-slate-700"
                      rows={4}
                      required
                    />
                  </div>
                  <Button type="submit" disabled={loading} className="w-full bg-slate-700 hover:bg-slate-600 py-3 rounded-full">
                    {loading ? "Sending..." : "Send Message"}
                  </Button>
                </form>
              ) : (
                <div className="text-center py-8">
                  <CheckCircle className="w-16 h-16 text-green-400 mx-auto mb-4" />
                  <p className="text-xl font-medium mb-2">Message Sent!</p>
                  <p className="text-slate-400">We'll get back to you as soon as possible.</p>
                </div>
              )}
            </motion.div>
          </div>
        </div>
      </section>

      <Footer />
      <BookingModal />
    </div>
  );
};

export default ContactPage;
